/** @jsxImportSource @emotion/react */

import React, { Component } from 'react';
import { css } from '@emotion/react';
import classNames from 'classnames';

import {
	getScrollbarWidth,
	checkDocument,
	getTransitionEndEvent,
} from '@utils/customLib';

class Draggable extends Component {
	drawer__wrapper = React.createRef();

	drawer__main = React.createRef();

	width__main = 0;

	startX = 0;

	startY = 0;

	currentX = 0;

	currentY = 0;

	touchingSideNav = false;

	transitionEndProperty = null;

	transitionEndTime = 0;

	continueY = null;

	componentDidUpdate(prevProps) {
		const { isOpened } = this.props;
		if (prevProps.isOpened !== isOpened) {
			if (isOpened) {
				this.showSideNav();
			} else {
				this.hideSideNav();
			}
		}
	}

	componentWillUnmount() {
		if (checkDocument()) {
			const $body = document.body;

			document.removeEventListener('keyup', this.listenToEscape);

			$body.classList.remove('drawer-shown');
			// $body.parentElement.classList.remove('drawer-shown');
			if (getScrollbarWidth() !== 0) {
				$body.style.marginRight = '';
			}
		}
	}

	onTransitionEnd = evt => {
		// console.log('transitionEnd', evt);
		if (
			evt.propertyName !== this.transitionEndProperty &&
			evt.elapsedTime !== this.transitionEndTime
		) {
			return;
		}

		if (checkDocument()) {
			const { isOpened } = this.props;
			const $body = document.body;

			if (isOpened) {
				document.addEventListener('keyup', this.listenToEscape);
				requestAnimationFrame(() => {
					const scrollbarWidth = getScrollbarWidth();

					if (
						window.innerHeight < $body.offsetHeight &&
						scrollbarWidth !== 0
					) {
						$body.style.marginRight = `${scrollbarWidth}px`;
					}
					$body.classList.add('drawer-shown');
					// $body.parentElement.classList.add('drawer-shown');
				});
			} else {
				document.removeEventListener('keyup', this.listenToEscape);

				requestAnimationFrame(() => {
					$body.classList.remove('drawer-shown');
					// $body.parentElement.classList.remove('drawer-shown');

					if (
						window.innerHeight < $body.offsetHeight &&
						getScrollbarWidth() !== 0
					) {
						$body.style.marginRight = '';
					}
					this.drawer__wrapper.current.classList.add(
						'pointer-events-none'
					);
				});
			}
		}

		this.transitionEndProperty = null;
		this.transitionEndTime = 0;

		this.drawer__wrapper.current.classList.remove('bb-drawer--animatable');
		// console.log(this.drawer__wrapper.current.classList);

		this.drawer__wrapper.current.removeEventListener(
			getTransitionEndEvent,
			this.onTransitionEnd
		);
	};

	showSideNav = () => {
		// console.log('showing side nav');
		this.drawer__wrapper.current.classList.add(
			'bb-drawer--animatable',
			'bb-drawer--open'
		);
		this.drawer__wrapper.current.classList.remove('pointer-events-none');

		// console.log(this.drawer__wrapper.current.classList);

		this.transitionEndProperty = 'transform';
		// the duration of transition (make unique to distinguish transitions )
		this.transitionEndTime = 0.33;
		if (getTransitionEndEvent) {
			this.drawer__wrapper.current.addEventListener(
				getTransitionEndEvent,
				this.onTransitionEnd
			);
		}
	};

	hideSideNav = () => {
		this.drawer__wrapper.current.classList.remove('bb-drawer--open');
		this.drawer__wrapper.current.classList.add('bb-drawer--animatable');
		// console.log(this.drawer__wrapper.current.classList);

		this.transitionEndProperty = 'transform';
		// the duration of transition (make unique to distinguish transitions )
		this.transitionEndTime = 0.13;

		if (getTransitionEndEvent) {
			this.drawer__wrapper.current.addEventListener(
				getTransitionEndEvent,
				this.onTransitionEnd
			);
		}
	};

	listenToEscape = e => {
		if (e.keyCode === 27) {
			this.props.closeSidenav();
		}
	};

	onTouchStart = e => {
		e.stopPropagation();
		const { isOpened } = this.props;

		if (!isOpened) return;
		this.width__main =
			this.drawer__main.current.getBoundingClientRect().width;
		this.startX = e.touches[0].pageX;
		this.startY = e.touches[0].pageY;
		this.currentX = this.startX;
		this.currentY = this.startY;

		requestAnimationFrame(this.update);

		this.touchingSideNav = true;
	};

	onTouchMove = e => {
		const { isOpened } = this.props;

		if (!isOpened && !this.touchingSideNav) return;
		this.currentX = e.touches[0].pageX;
		this.currentY = e.touches[0].pageY;

		if (this.continueY === null) {
			const translateX = Math.abs(this.currentX - this.startX);
			const translateY = Math.abs(this.currentY - this.startY);
			if (translateY < translateX) {
				this.continueY = false;
				this.drawer__main.current.style.overflow = 'hidden';
			} else {
				this.continueY = true;
				this.touchingSideNav = false;
				this.drawer__main.current.style.transform = '';
			}
		}
	};

	onTouchEnd = e => {
		const { pos, isOpened, closeSidenav } = this.props;

		if (!isOpened && !this.touchingSideNav) return;
		this.touchingSideNav = false;
		this.continueY = null;
		this.drawer__main.current.style.transform = '';
		this.drawer__main.current.style.overflow = '';

		const translateX = Math.abs(
			Math.min(
				pos === 'left'
					? Math.min(0, this.currentX - this.startX)
					: Math.max(0, this.currentX - this.startX)
			)
		);
		if (translateX > this.width__main / 3) {
			closeSidenav();
		}
	};

	update = () => {
		if (!this.touchingSideNav) {
			return;
		}
		const { pos } = this.props;

		const translateX =
			pos === 'left'
				? Math.min(0, this.currentX - this.startX)
				: Math.max(0, this.currentX - this.startX);

		this.drawer__main.current.style.transform = `translate3d(${translateX}px, 0, 0)`;
		requestAnimationFrame(this.update);
	};

	blockClicks = evt => {
		evt.stopPropagation();
	};

	render() {
		const { pos, width, closeSidenav, isOpened, ...childProps } =
			this.props;

		return (
			<aside
				className="bb-drawer pointer-events-none"
				ref={this.drawer__wrapper}
				onClick={closeSidenav}
				onTouchStart={this.onTouchStart}
				onTouchMove={this.onTouchMove}
				onTouchEnd={this.onTouchEnd}
				{...childProps}
			>
				<div
					className={classNames(
						{
							'bb-drawer__main--left': pos === 'left',
						},
						{
							'bb-drawer__main--right': pos === 'right',
						},
						'bb-drawer__main',
						'scrolling-touch',
						'bb-no-scroll-bar'
					)}
					css={css`
						width: ${width}px;
						max-width: 90vw;
					`}
					ref={this.drawer__main}
					onClick={this.blockClicks}
				>
					{childProps.children}
				</div>
			</aside>
		);
	}
}

export default Draggable;
